import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSettings } from '@/features/config/hooks/use-config';
import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { TextField } from '@/shared/ui/TextField';
import { useSessionStore } from '@/stores/session.store';

import { priorityColor, statusColor } from '../components/TicketDetail';
import { useBulkUpdateStatus, useTicketQueue } from '../hooks/use-support';
import {
  hoursSinceCreated,
  isOverdue,
  slaSettingKeyForPriority,
  SUPPORT_TICKET_CATEGORIES,
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
  type SupportTicket,
  type SupportTicketCategory,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from '../model/support-ticket';

type StatusFilter = SupportTicketStatus | 'all';
type PriorityFilter = SupportTicketPriority | 'all';
type CategoryFilter = SupportTicketCategory | 'all';

export function AdminTicketsRoute(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useSessionStore((s) => s.userId);
  const settings = useAppSettings();
  const [status, setStatus] = useState<StatusFilter>('open');
  const [priority, setPriority] = useState<PriorityFilter>('all');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [mine, setMine] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<SupportTicketStatus>('in_progress');

  // Debounce the subject search so every keystroke doesn't trigger a query.
  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const filter = {
    status,
    priority,
    category,
    assignedToMe: mine,
    currentUserId: userId ?? undefined,
    search: search.length > 0 ? search : undefined,
  };
  const query = useTicketQueue(filter);
  const bulkUpdate = useBulkUpdateStatus();

  const list = useMemo(() => query.data?.pages.flat() ?? [], [query.data]);

  function toggleSelected(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection(): void {
    setSelected(new Set());
  }

  function applyBulk(): void {
    if (selected.size === 0) return;
    bulkUpdate.mutate(
      { ids: Array.from(selected), status: bulkStatus },
      { onSuccess: clearSelection },
    );
  }

  return (
    <main className="page stack">
      <h1>{t('support.inboxTitle')}</h1>

      <div className="row wrap">
        <label className="field" style={{ minWidth: 160 }}>
          <span className="field__label">{t('support.filterStatus')}</span>
          <select
            className="field__input"
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
          >
            <option value="all">{t('support.status_all')}</option>
            {SUPPORT_TICKET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`support.status_${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 160 }}>
          <span className="field__label">{t('support.filterPriority')}</span>
          <select
            className="field__input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as PriorityFilter)}
          >
            <option value="all">{t('support.priority_all')}</option>
            {SUPPORT_TICKET_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`support.priority_${p}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 160 }}>
          <span className="field__label">{t('support.filterCategory')}</span>
          <select
            className="field__input"
            value={category}
            onChange={(e) => setCategory(e.target.value as CategoryFilter)}
          >
            <option value="all">{t('support.category_all')}</option>
            {SUPPORT_TICKET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`support.category_${c}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="row" style={{ gap: 'var(--space-2)' }}>
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          <span>{t('support.filterAssignedToMe')}</span>
        </label>
      </div>

      <TextField
        label={t('support.searchLabel')}
        placeholder={t('support.searchPlaceholder')}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />

      {selected.size > 0 ? (
        <div className="card row row--between wrap">
          <span>{t('support.bulkSelected', { count: selected.size })}</span>
          <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
            <label className="field" style={{ minWidth: 160 }}>
              <span className="field__label">{t('support.bulkStatusLabel')}</span>
              <select
                className="field__input"
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value as SupportTicketStatus)}
              >
                {SUPPORT_TICKET_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`support.status_${s}`)}
                  </option>
                ))}
              </select>
            </label>
            <Button loading={bulkUpdate.isPending} onClick={applyBulk}>
              {t('support.bulkApply')}
            </Button>
            <Button variant="text" onClick={clearSelection}>
              {t('support.clearSelection')}
            </Button>
          </div>
        </div>
      ) : null}

      <Async query={query} isEmpty={() => list.length === 0} emptyMessage={t('support.inboxEmpty')}>
        {() => (
          <div className="stack">
            {list.map((tk) => {
              const slaHours = settings.num(slaSettingKeyForPriority(tk.priority));
              const overdue = isOverdue(tk, slaHours);
              return (
                <TicketRow
                  key={tk.id}
                  ticket={tk}
                  selected={selected.has(tk.id)}
                  overdue={overdue}
                  onToggleSelect={() => toggleSelected(tk.id)}
                  onOpen={() => navigate(`/support/tickets/${tk.id}`)}
                />
              );
            })}
            {query.hasNextPage ? (
              <Button
                variant="outline"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('support.loadMore')}
              </Button>
            ) : null}
          </div>
        )}
      </Async>
    </main>
  );
}

function TicketRow({
  ticket,
  selected,
  overdue,
  onToggleSelect,
  onOpen,
}: {
  ticket: SupportTicket;
  selected: boolean;
  overdue: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const age = hoursSinceCreated(ticket.createdAt);

  return (
    <div className="card stack">
      <div className="row row--between">
        <label className="row" style={{ gap: 'var(--space-2)' }}>
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={t('support.selectTicket', { subject: ticket.subject })}
          />
        </label>
        <button
          type="button"
          className="card--interactive stack"
          style={{ flex: 1, border: 'none', background: 'none', padding: 0 }}
          onClick={onOpen}
        >
          <div className="row row--between">
            <strong>{ticket.subject}</strong>
            <div className="wrap">
              <Badge
                label={t(`support.status_${ticket.status}`)}
                color={statusColor(ticket.status)}
              />
              <Badge
                label={t(`support.priority_${ticket.priority}`)}
                color={priorityColor(ticket.priority)}
              />
              {overdue ? <Badge label={t('support.overdue')} color="var(--color-danger)" /> : null}
            </div>
          </div>
          <div className="wrap">
            {ticket.createdByName ? (
              <span className="muted">{t('support.filedBy', { name: ticket.createdByName })}</span>
            ) : null}
            <Badge
              label={t(`support.category_${ticket.category}`)}
              color="var(--color-on-surface-muted)"
            />
            {ticket.assignedAdminName ? (
              <span className="muted">
                {t('support.assignedTo', { name: ticket.assignedAdminName })}
              </span>
            ) : null}
            <span className="muted">{new Date(ticket.createdAt).toLocaleDateString()}</span>
            <span className="muted">{t('support.slaAge', { hours: age })}</span>
          </div>
        </button>
      </div>
    </div>
  );
}
